/**
 * Helpers that decide when the Farm View map genuinely needs a full repaint.
 *
 * Why this exists: a single field selection in Farm View fans out into 3–5
 * React renders in quick succession — the local `selectedFieldId` state, the
 * Redux `selected_entities` dispatch, the field-history load transitioning
 * init→success, and the NDVI arrival rewriting `distributions` (which gives
 * `farmingUnitForMap` a fresh reference). The map-update effect depends on all
 * of them, so it fired `updateMap()` (a full `CustomMap.invalidate`) on every
 * tick. Each invalidate re-applied the base layer and reloaded the satellite
 * tiles, so the whole map blinked once per tick — the reported "flickers 3–5
 * times when changing the selected field".
 *
 * `buildMapRepaintSignature` collapses those redundant ticks: it captures every
 * input that actually changes what the map draws. The effect repaints only when
 * the signature differs from the last painted one, mirroring the
 * meaningful-transition guard already used by `MapContainer.componentDidUpdate`
 * in Field View.
 *
 * Pure module (no React / DOM / browser APIs) so it is unit-testable with
 * `node --test`.
 */

/**
 * Build a stable string key describing the map-relevant state. Two calls with
 * the same map-affecting inputs return an identical string; any change that the
 * map must reflect (field selection, archive mode, NDVI availability, a history
 * status settling to 'success') changes it.
 *
 * Note: `historyStatusUpdatedAt` timestamps are deliberately NOT part of the
 * signature — they bump on every history status transition (including the
 * init→init re-dispatches that cause the flicker) without changing what is
 * drawn. The stable `fieldHistoryStatus` / `farmingUnitHistoryStatus` strings
 * capture the only transition the map cares about (→ 'success').
 *
 * @param {Object} params
 * @param {string} [params.farmingUnitId]
 * @param {string|null} [params.selectedFieldId]
 * @param {boolean} [params.archiveMode]
 * @param {boolean} [params.hasFieldNdvi] - selected field has NDVI tiles
 * @param {boolean} [params.hasFarmingUnitNdvi] - farming unit has NDVI tiles
 * @param {string} [params.fieldHistoryStatus] - '' | 'init' | 'success'
 * @param {string} [params.farmingUnitHistoryStatus] - '' | 'init' | 'success'
 * @returns {string}
 */
const buildMapRepaintSignature = (params) => {
  const {
    farmingUnitId,
    selectedFieldId,
    archiveMode,
    hasFieldNdvi,
    hasFarmingUnitNdvi,
    fieldHistoryStatus,
    farmingUnitHistoryStatus,
  } = params || {};

  return [
    farmingUnitId ?? '',
    selectedFieldId ?? '',
    archiveMode ? 'archive' : 'active',
    hasFieldNdvi ? 'fndvi' : '',
    hasFarmingUnitNdvi ? 'fundvi' : '',
    fieldHistoryStatus ?? '',
    farmingUnitHistoryStatus ?? '',
  ].join('|');
};

module.exports = {
  buildMapRepaintSignature,
};
